import dayjs, { ManipulateType, OpUnitType } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

import { FilterPreset } from '@cast/design-system';
import { TimeRange } from '@cast/shared-types';

import {
  DateRangeFilter,
  RangePickerQueryParamKeys,
} from '../types/time-range';

dayjs.extend(timezone);

const defaultTimezone = dayjs.tz.guess();

const getPreviousRange = (
  count: number,
  unit: ManipulateType,
  options: { timezone?: string } = {}
): TimeRange<string> => {
  const from = dayjs().tz(options.timezone).subtract(count, unit).startOf(unit);
  return {
    from: from.toISOString(),
    to: from.endOf(unit).toISOString(),
  };
};

const getRollingRange = (
  count: number,
  unit: ManipulateType,
  options: { timezone?: string } = {}
): TimeRange<string> => {
  const now = dayjs().tz(options.timezone);
  return {
    from: now.subtract(count, unit).toISOString(),
    to: now.toISOString(),
  };
};

const getPaddedRollingRange = (
  count: number,
  unit: ManipulateType,
  options: { timezone?: string } = {}
): TimeRange<string> => {
  const now = dayjs().tz(options.timezone);
  return {
    from: now.subtract(count, unit).add(1, 'day').startOf('day').toISOString(),
    to: now.endOf('day').toISOString(),
  };
};

const getBoundingRange = (
  unit: OpUnitType,
  options: { timezone?: string } = {}
): TimeRange<string> => {
  const now = dayjs().tz(options.timezone);
  return {
    from: now.startOf(unit).toISOString(),
    to: now.endOf(unit).toISOString(),
  };
};

export const allFilters: Record<DateRangeFilter, FilterPreset> = {
  [DateRangeFilter.ThisMonth]: {
    label: 'This month',
    date: (timezone = defaultTimezone) =>
      getBoundingRange('month', { timezone }),
  },
  [DateRangeFilter.Last15Mins]: {
    label: 'Last 15 minutes',
    date: (timezone) => getRollingRange(15, 'minutes', { timezone }),
  },
  [DateRangeFilter.Last30Mins]: {
    label: 'Last 30 minutes',
    date: (timezone) => getRollingRange(30, 'minutes', { timezone }),
  },
  [DateRangeFilter.LastHour]: {
    label: 'Last hour',
    date: (timezone) => getRollingRange(1, 'hour', { timezone }),
  },
  [DateRangeFilter.Last6Hours]: {
    label: 'Last 6 hours',
    date: (timezone) => getRollingRange(6, 'hours', { timezone }),
  },
  [DateRangeFilter.Last24Hours]: {
    label: 'Last 24 hours',
    date: (timezone) => getRollingRange(24, 'hours', { timezone }),
  },
  [DateRangeFilter.Today]: {
    label: 'Today',
    date: (timezone) => getBoundingRange('day', { timezone }),
  },
  [DateRangeFilter.Last7Days]: {
    label: 'Last 7 days',
    date: (timezone, fromNow) => {
      return fromNow
        ? getRollingRange(7, 'days', { timezone })
        : getPaddedRollingRange(7, 'days', { timezone });
    },
  },
  [DateRangeFilter.Last30Days]: {
    label: 'Last 30 days',
    date: (timezone, fromNow) => {
      return fromNow
        ? getRollingRange(30, 'days', { timezone })
        : getPaddedRollingRange(30, 'days', { timezone });
    },
  },
  [DateRangeFilter.PreviousMonth]: {
    label: 'Previous month',
    date: (timezone) => getPreviousRange(1, 'month', { timezone }),
  },
  [DateRangeFilter.PreviousWeek]: {
    label: 'Previous week',
    date: (timezone) => getPreviousRange(1, 'week', { timezone }),
  },
  [DateRangeFilter.Last2Weeks]: {
    label: 'Last 2 weeks',
    date: (timezone, fromNow) => {
      return fromNow
        ? getRollingRange(2, 'weeks', { timezone })
        : getPaddedRollingRange(2, 'weeks', { timezone });
    },
  },
  [DateRangeFilter.Last3Months]: {
    label: 'Last 3 months',
    date: (timezone, fromNow) => {
      return fromNow
        ? getRollingRange(3, 'months', { timezone })
        : getPaddedRollingRange(3, 'months', { timezone });
    },
  },
  [DateRangeFilter.Last6Months]: {
    label: 'Last 6 months',
    date: (timezone, fromNow) => {
      return fromNow
        ? getRollingRange(6, 'months', { timezone })
        : getPaddedRollingRange(6, 'months', { timezone });
    },
  },
  [DateRangeFilter.Last9Months]: {
    label: 'Last 9 months',
    date: (timezone, fromNow) => {
      return fromNow
        ? getRollingRange(9, 'months', { timezone })
        : getPaddedRollingRange(9, 'months', { timezone });
    },
  },
  [DateRangeFilter.Last12Months]: {
    label: 'Last 12 months',
    date: (timezone, fromNow) => {
      return fromNow
        ? getRollingRange(12, 'months', { timezone })
        : getPaddedRollingRange(12, 'months', { timezone });
    },
  },
};

export const defaultQueryParamKeys: RangePickerQueryParamKeys = {
  fromDate: 'date_from',
  toDate: 'date_to',
  dateRangePreset: 'date_preset',
  timeZone: 'timezone',
};
